import { Injectable } from '@angular/core';
import * as TemplateLibraryData from '../containers/editor/scene-blocks/card-constants.json';

export interface ITemplateBlock {
    x: number;
    y: number;
    w: number;
    h: number;
    type: string;
    special?: boolean;
    lines?: number;
    rounded?: boolean;
    isMimeType?: boolean;
    chartType?: string;
    iconType?: string;
}

export interface ISceneTemplate {
    id: string;
    name: string;
    icon: string;
    category: string;
    resolution: string;
    width: number;
    height: number;
    description: string;
    tags: string[];
    blocks: ITemplateBlock[];
}

export interface ITemplateResolution {
    name: string;
    width: number;
    height: number;
    aspectRatio: string;
    orientation: string;
    description: string;
}

export interface ITemplateCategory {
    name: string;
    icon: string;
    description: string;
}

interface ITemplateLibrary {
    version: string;
    description: string;
    metadata: {
        totalTemplates: number;
        lastUpdated: string;
        author: string;
        designPrinciples?: string;
    };
    resolutions: { [key: string]: ITemplateResolution };
    categories: { [key: string]: ITemplateCategory };
    templates: ISceneTemplate[];
}

@Injectable({
    providedIn: 'root'
})
export class SceneCardTemplateService {
    private library: ITemplateLibrary;
    private currentTemplate: ISceneTemplate | null = null;

    constructor() {
        // Load template library from JSON
        this.library = TemplateLibraryData as any as ITemplateLibrary;
        console.log(`[SceneCardTemplateService] Loaded ${this.library.templates.length} templates from library v${this.library.version}`);
    }

    /**
     * Get all templates
     */
    getTemplates(): ISceneTemplate[] {
        return this.library.templates;
    }

    /**
     * Get templates by category
     */
    getTemplatesByCategory(category: string): ISceneTemplate[] {
        return this.library.templates.filter(t => t.category === category);
    }

    /**
     * Get templates by resolution
     */
    getTemplatesByResolution(resolution: string): ISceneTemplate[] {
        return this.library.templates.filter(t => t.resolution === resolution);
    }

    /**
     * Get templates by category AND resolution
     */
    getTemplatesByCategoryAndResolution(category: string, resolution: string): ISceneTemplate[] {
        return this.library.templates.filter(t =>
            t.category === category && t.resolution === resolution
        );
    }

    /**
     * Get templates grouped by category
     */
    getTemplatesByCategories(): { [category: string]: ISceneTemplate[] } {
        const grouped: { [category: string]: ISceneTemplate[] } = {};

        this.library.templates.forEach(template => {
            if (!grouped[template.category]) {
                grouped[template.category] = [];
            }
            grouped[template.category].push(template);
        });

        return grouped;
    }

    /**
     * Get templates grouped by resolution
     */
    getTemplatesByResolutions(): { [resolution: string]: ISceneTemplate[] } {
        const grouped: { [resolution: string]: ISceneTemplate[] } = {};

        this.library.templates.forEach(template => {
            if (!grouped[template.resolution]) {
                grouped[template.resolution] = [];
            }
            grouped[template.resolution].push(template);
        });

        return grouped;
    }

    /**
     * Get all category names
     */
    getCategoryNames(): string[] {
        return Object.keys(this.library.categories);
    }

    /**
     * Get all resolution names
     */
    getResolutionNames(): string[] {
        return Object.keys(this.library.resolutions);
    }

    /**
     * Get category info
     */
    getCategoryInfo(category: string): ITemplateCategory | undefined {
        return this.library.categories[category];
    }

    /**
     * Get resolution info
     */
    getResolutionInfo(resolution: string): ITemplateResolution | undefined {
        return this.library.resolutions[resolution];
    }

    /**
     * Search templates by name, tags, or description
     */
    searchTemplates(query: string): ISceneTemplate[] {
        const lowerQuery = query.toLowerCase();
        return this.library.templates.filter(t =>
            t.name.toLowerCase().includes(lowerQuery) ||
            t.description.toLowerCase().includes(lowerQuery) ||
            t.tags.some(tag => tag.toLowerCase().includes(lowerQuery)) ||
            t.category.toLowerCase().includes(lowerQuery)
        );
    }

    /**
     * Get a random template
     */
    getRandomTemplate(): ISceneTemplate {
        const index = Math.floor(Math.random() * this.library.templates.length);
        return this.library.templates[index];
    }

    /**
     * Get template by ID
     */
    getTemplateById(id: string): ISceneTemplate | undefined {
        return this.library.templates.find(t => t.id === id);
    }

    /**
     * Get current selected template
     */
    getCurrentTemplate(): ISceneTemplate | null {
        // Try to load from localStorage
        if (!this.currentTemplate) {
            const saved = localStorage.getItem('selected-template');
            if (saved) {
                try {
                    const savedData = JSON.parse(saved);
                    this.currentTemplate = this.getTemplateById(savedData.id) || null;
                } catch (e) {
                    console.warn('[SceneCardTemplateService] Failed to parse saved template:', e);
                }
            }
        }
        return this.currentTemplate;
    }

    /**
     * Set current selected template
     */
    setCurrentTemplate(template: ISceneTemplate): void {
        this.currentTemplate = template;
        // Save to localStorage
        localStorage.setItem('selected-template', JSON.stringify({ id: template.id }));
        console.log(`[SceneCardTemplateService] Selected template: ${template.name}`);
    }

    /**
     * Get library metadata
     */
    getMetadata() {
        return {
            version: this.library.version,
            description: this.library.description,
            totalTemplates: this.library.metadata.totalTemplates,
            totalCategories: Object.keys(this.library.categories).length,
            totalResolutions: Object.keys(this.library.resolutions).length,
            lastUpdated: this.library.metadata.lastUpdated,
            author: this.library.metadata.author
        };
    }
}
